// Zoom math for the interactive map (src/pages/InteractiveMapPage.jsx).
//
// The map div sits at the container's centre and is transformed with
// `translate(calc(-50% + position.x), calc(-50% + position.y)) scale(scale)` about its own
// centre, so a map-local point `v` (unscaled px measured from the map's centre) projects to:
//
//   screen = containerCentre + position + scale * v
//
// Changing `scale` alone therefore pins the *map's* centre, not the *viewport's* — once the
// user has panned, the view slides out from under them. `zoomAnchored` compensates by moving
// `position` so that whichever point sits under the anchor stays under the anchor.
//
// Kept in its own module (pure, no React) so tests/map-zoom.test.js can exercise it without a
// DOM — the suite runs in Vitest's `node` environment.

export const MIN_SCALE = 0.5
export const MAX_SCALE = 5

export function clampScale(scale) {
  if (Number.isNaN(scale)) return MIN_SCALE
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale))
}

/**
 * Compute the next {scale, position} for a zoom that holds one screen point still.
 *
 * @param {number} scale       current scale
 * @param {{x:number,y:number}} position  current pan offset, in screen px
 * @param {number} nextScale   requested scale (clamped to [MIN_SCALE, MAX_SCALE])
 * @param {{x:number,y:number}|null} center  container centre in client coords
 * @param {{x:number,y:number}} [anchor]  point to hold fixed, in client coords.
 *                                        Defaults to `center` (i.e. keep the view centred).
 */
export function zoomAnchored({ scale, position, nextScale, center, anchor }) {
  const clamped = clampScale(nextScale)
  if (clamped === scale) return { scale, position }

  // No geometry to anchor against (container not mounted yet) — just change the scale.
  if (!center) return { scale: clamped, position }

  const a = anchor || center
  const ratio = clamped / scale

  // Offset from the map's centre to the anchor, in screen px.
  const dx = a.x - center.x - position.x
  const dy = a.y - center.y - position.y

  // That offset grows by `ratio` when scaling, so shift `position` back by the difference.
  return {
    scale: clamped,
    position: {
      x: position.x + dx * (1 - ratio),
      y: position.y + dy * (1 - ratio),
    },
  }
}
